/* eslint-disable @trello/disallow-filenames */
import moment from 'moment';

import { featureFlagClient } from '@trello/feature-flag-client';

import { FilterMode } from 'app/src/components/ViewFilters/types';

const dueMap = {
  day: 1,
  week: 7,
  month: 28,
};

export const ID_NONE = 'none';
// Value used to filter for cards with no labels; maps to a translation.
export const NO_LABELS = 'no labels';

export interface Filterable {
  idMembers: string[];
  idLabels: string[];
  due: Date | null;
  complete: boolean;
  words: string[];
}

export interface Filter {
  idMembers?: string[];
  idLabels?: string[];
  mode?: FilterMode;
  due?: 'notdue' | keyof typeof dueMap;
  overdue?: boolean;
  dueComplete?: boolean;
  title?: string;
}

// Treat all whitespace an punctuation as word separators, except...
// # (for searches like #123)
// % (for searches like 100%)
// ' (for searches like can't)
// - (for searches like RMK-123)
// . and @ (for searches like taco@trello.com)
export const reWordSeparators = /[\s!"$&()*+,/:;<=>?[\\\]^_`{|}~]/;

export function getWords(s: string | undefined): string[] {
  return s
    ? s
        .toLowerCase()
        .split(reWordSeparators)
        .filter((word) => word)
    : [];
}

/**
 * Compare a given word against a set of filterable words. For example, the word
 * "super" should match a card named "Superman" and cards labeled "superhero".
 * Breaks the given word into tokens and returns a function that returns true
 * if the tokens match anything in the filterableWords input. Note that this
 * returns void if the initial word cannot be tokenized; this is meaningfully
 * not a noop in order to indicate that there isn't a need to run the filter.
 */
export function getSatisfiesWordFilter(
  word: string,
): (filterableWords: string[]) => boolean {
  // Want to match at least one word from our search
  const searchWords = getWords(word);

  if (searchWords.length === 0) {
    return () => true;
  }

  const endsWithPartialWord = !reWordSeparators.test(word[word.length - 1]);

  const completeWords = endsWithPartialWord
    ? searchWords.slice(0, searchWords.length - 1)
    : searchWords;
  const partialWord = endsWithPartialWord
    ? searchWords[searchWords.length - 1]
    : undefined;

  return (filterableWords: string[]) => {
    const actualWordsSet = new Set(filterableWords);
    const matchesAnyWord =
      completeWords.some((word) => actualWordsSet.has(word)) ||
      (partialWord &&
        filterableWords.some((word) => word.startsWith(partialWord)));
    return !!matchesAnyWord;
  };
}

function satisfiesLabelsOrMembersFilter(
  filterableArr: string[],
  filterArr: string[],
  mode: Filter['mode'],
) {
  const effective = filterableArr.length === 0 ? [ID_NONE] : filterableArr;

  if (mode === FilterMode.And) {
    // Account for no labels/members if they are the only active filters
    if (filterArr.length === 1 && filterArr[0] === ID_NONE) {
      return filterableArr.length === 0;
    }

    // We need all of the filter values
    const actualSet = new Set(filterableArr);
    return filterArr.every((value) => actualSet.has(value));
  } else {
    // We need at least one of the filter values
    const requiredSet = new Set(filterArr);
    return effective.some((value) => requiredSet.has(value));
  }
}

function satisfiesDueFilter(
  { due, complete }: Filterable,
  { due: filterDue, dueComplete, overdue }: Filter,
) {
  if (dueComplete === false && (complete || !due)) {
    return false;
  }

  if (dueComplete && !complete) {
    return false;
  }

  if (filterDue) {
    switch (filterDue) {
      case 'notdue':
        if (due !== null) return false;
        break;

      default: {
        if (!due) {
          return false;
        } else {
          const maxDate = moment().add(dueMap[filterDue], 'day');
          const cardDueMoment = moment(due);

          const failsDateCheck = overdue
            ? cardDueMoment.isAfter(maxDate) || complete
            : !cardDueMoment.isBetween(Date.now(), maxDate);
          if (failsDateCheck) {
            return false;
          }
        }

        return true;
      }
    }
  }

  if (overdue && (!due || complete || moment(due).isAfter(Date.now()))) {
    return false;
  }

  return true;
}
function satisfiesTitleFilter(words: string[], title: string) {
  return getSatisfiesWordFilter(title)(words);
}

function satisfiesBritaFilter(filterable: Filterable, filter: Filter): boolean {
  const isAnd = filter.mode === FilterMode.And;

  const isDueActive =
    filter.due !== undefined ||
    filter.dueComplete !== undefined ||
    filter.overdue !== undefined;

  const activeFilters = [];

  if (filter.idLabels && filter.idLabels.length > 0) {
    activeFilters.push(
      satisfiesLabelsOrMembersFilter(
        filterable.idLabels || [],
        filter.idLabels,
        filter.mode,
      ),
    );
  }

  if (filter.idMembers && filter.idMembers.length > 0) {
    activeFilters.push(
      satisfiesLabelsOrMembersFilter(
        filterable.idMembers || [],
        filter.idMembers,
        filter.mode,
      ),
    );
  }

  if (isDueActive) {
    activeFilters.push(satisfiesDueFilter(filterable, filter));
  }

  if (filter.title) {
    activeFilters.push(satisfiesTitleFilter(filterable.words, filter.title));
  }

  if (activeFilters.length === 0) {
    return true;
  }

  return isAnd ? activeFilters.every(Boolean) : activeFilters.some(Boolean);
}

function satisfiesNonBritaFilter(
  filterable: Filterable,
  filter: Filter,
): boolean {
  const matchesLabelsAndMembers = [
    { required: filter.idLabels, actual: filterable.idLabels || [] },
    { required: filter.idMembers, actual: filterable.idMembers || [] },
  ].every(({ required, actual }) => {
    if (!required || required.length === 0) {
      return true;
    }

    const effective = actual.length === 0 ? [ID_NONE] : actual;

    if (filter.mode === FilterMode.And) {
      // Account for no labels/members if they are the only active filters
      if (required.length === 1 && required[0] === ID_NONE) {
        return actual.length === 0;
      }

      // We need all of the required values
      const actualSet = new Set(actual);
      return required.every((value) => actualSet.has(value));
    } else {
      // We need at least one of the required values
      const requiredSet = new Set(required);
      return effective.some((value) => requiredSet.has(value));
    }
  });

  if (!matchesLabelsAndMembers) {
    return false;
  }

  if (filter.title) {
    if (!getSatisfiesWordFilter(filter.title)(filterable.words)) {
      return false;
    }
  }

  if (
    filter.dueComplete !== undefined &&
    filter.dueComplete === !filterable.complete
  ) {
    return false;
  }

  if (filter.due) {
    switch (filter.due) {
      case 'notdue':
        if (filterable.due !== null) return false;
        break;

      default: {
        if (!filterable.due) {
          return false;
        } else {
          const maxDate = moment().add(dueMap[filter.due], 'day');
          const cardDueMoment = moment(filterable.due);

          const failsDateCheck = filter.overdue
            ? cardDueMoment.isAfter(maxDate) || filterable.complete
            : !cardDueMoment.isBetween(Date.now(), maxDate);
          if (failsDateCheck) {
            return false;
          }
        }

        return true;
      }
    }
  }

  if (
    filter.overdue &&
    (!filterable.due ||
      filterable.complete ||
      moment(filterable.due).isAfter(Date.now()))
  ) {
    return false;
  }

  return true;
}

export function satisfiesFilter(
  filterable: Filterable,
  filter: Filter,
): boolean {
  const isBritaModeEnabled = featureFlagClient.get(
    'remarkable.project-brita',
    false,
  );

  // Bifurcating for the sake of keeping updates to and/or filtering logic
  // behind a feature flag. Once Brita is fully rolled out, satisfiesNonBritaFilter
  // will be removed, and satisfiesFilter will just be satisfiesBritaFilter

  // Logic difference:
  // NonBrita takes the and/or parameter and only applies it within labels and members.
  // ex: and -> must satisfy green label and red label, but has no bearing on due or title
  // Brita takes the and/or parameter and applies it across all categories.
  // ex: and -> must satisfy green label and Michael Member and title = 'search term'
  return isBritaModeEnabled
    ? satisfiesBritaFilter(filterable, filter)
    : satisfiesNonBritaFilter(filterable, filter);
}
